import {fold} from 'fp-ts/lib/Monoid';
import * as O from 'fp-ts/lib/Option';
import {getDualOrd, getMonoid, ord, ordNumber} from 'fp-ts/lib/Ord';
import {
  BehaviorSubject,
  defer,
  EMPTY,
  MonoTypeOperatorFunction,
  noop,
  Observable,
  of,
  OperatorFunction,
} from 'rxjs';
import {
  catchError,
  concatMap,
  distinctUntilChanged,
  filter,
  map,
  mapTo,
  switchMap,
  tap,
} from 'rxjs/operators';

import {TaskSourcesEnum} from '../../../../libs/shared/helpers/src/instrumentation/eventLoopPerformanceHelpers';
import {logger} from '../../../../srcCommon/services/logger/logger';
import {prefetchConversationAsyncAction} from '../../../../srcCommon/services/offline/offlineConversationActions';
import {offlineConversationContents} from '../../../../srcCommon/services/offline/offlineConversationContents';
import {inIdleOrHiddenCallbackAsync} from '../../../helpers/async/inIdleCallback';
import {ExponentialBackoffOptions, withExponentialBackoff} from '../../../helpers/controlHelpers';
import {findContentUpdatedAt} from '../../../helpers/models/conversationHelpers';
import {createPriorityQueueWithComparator, PriorityQueue} from '../../../helpers/queueHelpers';
import {ConversationModel} from '../../../models/api/conversationModel';
import {
  ConversationTrackerStagesEnum,
  ConversationTrackerStatusesEnum,
} from '../../../models/api/conversationTrackerModel';
import {AppState} from '../../../store/appState';
import {ResourceMap} from '../../../store/appTypes';
import {getConversations} from '../../../store/data/dataSelectors';
import {getUserId} from '../../../store/data/teammates/teammatesSelectors';
import {
  getHasBootConversationContentPrefetch,
  getHasNewConversationContentPrefetch,
} from '../../../store/featureFlags/featureFlagsSelectors';
import {isHttpTooManyRequestsError} from '../../http/httpError';
import {Prefetcher} from './base';

const prefetchLogger = logger.getLogger('prefetch');

/*
 * Factory.
 */

export interface ConversationContentPrefetcher extends Prefetcher<PrefetchItem> {
  readonly prefetchedBumpInterval: number;
}

export function createConversationContentPrefetcher(
  bootState: AppState,
  exponentialBackoffOptions: ExponentialBackoffOptions,
): ConversationContentPrefetcher {
  // Track the range of conversations that we prefetched (or encountered cache hits for).
  let minPrefetchedBumpedAt = Date.now();
  let maxPrefetchedBumpedAt = 0;

  const onCachedItem = (item: PrefetchItem) => {
    minPrefetchedBumpedAt = Math.min(item.bumpedAt, minPrefetchedBumpedAt);
    maxPrefetchedBumpedAt = Math.max(item.bumpedAt, maxPrefetchedBumpedAt);
  };

  const state$ = new BehaviorSubject<AppState>(bootState);
  const items$ = fromConversationContentUpdates(bootState, state$, onCachedItem);
  const queue = createPrefetchQueue(state$, items$);

  // TODO: Remove this warning when we verified prefetching after restart is working correctly.
  if (queue && queue.length > 0) {
    prefetchLogger.warn('Started with timeline prefetcher backlog', {size: queue.length});
  }

  return {
    key: 'timeline',
    computeBacklogLength: (state) => {
      state$.next(state);

      popOutdatedPrefetchItems(queue, state);
      return queue.length;
    },
    computeCondition: (state) => {
      state$.next(state);

      popOutdatedPrefetchItems(queue, state);
      return O.fromNullable(queue.peek());
    },
    C: ordConversationContentItem,
    prefetchAsync: async (dispatch, item) => {
      prefetchLogger.debug('Prefetcher dequeued a conversation', {item});

      const isCached = await withExponentialBackoff(
        () => dispatch(prefetchConversationAsyncAction(item.conversationId)),
        isHttpTooManyRequestsError,
        exponentialBackoffOptions,
      );

      if (queue.peek() === item) {
        queue.pop();
      }

      if (isCached) {
        onCachedItem(item);
      }
    },
    get prefetchedBumpInterval() {
      return Math.max(0, maxPrefetchedBumpedAt - minPrefetchedBumpedAt);
    },
  };
}

/*
 * Helpers.
 */

type PrefetchItem = {
  conversationId: number;
  contentUpdatedAt: number;
  bumpedAt: number;
};

const dualOrdNumber = getDualOrd(ordNumber);

// Sort items by bumpedAt descending, contentUpdatedAt descending, conversationId descending.
const ordConversationContentItem = fold(getMonoid<PrefetchItem>())([
  ord.contramap(dualOrdNumber, (item) => item.bumpedAt),
  ord.contramap(dualOrdNumber, (item) => item.contentUpdatedAt),
  ord.contramap(dualOrdNumber, (item) => item.conversationId),
]);

/** Creates an observable that emits prefetcher items based on changes to the app state and what's in the cache. */
function fromConversationContentUpdates(
  bootState: AppState,
  state$: Observable<AppState>,
  onCachedItem: (item: PrefetchItem) => void = noop,
): Observable<PrefetchItem> {
  const shouldPrefetchInitialState = (initialState: AppState) =>
    initialState === bootState && getHasBootConversationContentPrefetch(initialState);

  return state$.pipe(
    flatMapToPrefetchItems(shouldPrefetchInitialState),
    filterPrefetchItemNotCached(onCachedItem),
  );
}

/** Emits prefetcher items for new and updated conversations in the app state. */
function flatMapToPrefetchItems(
  shouldPrefetchInitialState: (initialState: AppState) => boolean,
): OperatorFunction<AppState, PrefetchItem> {
  return (source$) =>
    new Observable((subscriber) => {
      // So we don't have to keep the previous array of conversations in memory.
      const prevContentUpdatedDates = new Map<number, number>();
      let isInitialState = true;

      return source$.subscribe(
        (state) => {
          try {
            const userId = getUserId(state);
            const conversations = getConversations(state);

            Object.values(conversations).forEach((conversation) => {
              const item = maybeCreatePrefetchItem(userId, conversation);
              if (!item) {
                return;
              }

              const prevContentUpdatedAt = prevContentUpdatedDates.get(item.conversationId);
              if (prevContentUpdatedAt && prevContentUpdatedAt >= item.contentUpdatedAt) {
                return;
              }

              prevContentUpdatedDates.set(item.conversationId, item.contentUpdatedAt);

              if (isInitialState && !shouldPrefetchInitialState(state)) {
                return;
              }

              subscriber.next(item);
            });

            isInitialState = false;
          } catch (error) {
            subscriber.error(error);
          }
        },
        subscriber.error,
        subscriber.complete,
      );
    });
}

/** Returns a queue item if the given conversation should be prefetched. */
function maybeCreatePrefetchItem(
  userId: number,
  conversation: ConversationModel | undefined,
): PrefetchItem | undefined {
  if (!conversation) {
    return undefined;
  }

  // We only care about conversations that are tracker open.
  const tracker = conversation.trackers[userId];
  if (
    !tracker ||
    tracker.stage === ConversationTrackerStagesEnum.NONE ||
    tracker.status !== ConversationTrackerStatusesEnum.INBOX
  ) {
    return undefined;
  }

  // We only care about conversations that support P3 caching.
  const contentUpdatedAt = findContentUpdatedAt(conversation.contentUpdatedDates, userId);
  if (!contentUpdatedAt) {
    return undefined;
  }

  return {
    conversationId: conversation.id,
    bumpedAt: tracker.bumpedAt,
    contentUpdatedAt,
  };
}

/** Keeps only prefetcher items that do not exist in the cache, by asynchronously querying IndexedDB. */
function filterPrefetchItemNotCached(
  onCachedItem: (item: PrefetchItem) => void = noop,
): MonoTypeOperatorFunction<PrefetchItem> {
  return concatMap((item) =>
    // Fetch the item from cache in an idle callback as a way to throttle.
    defer(() =>
      inIdleOrHiddenCallbackAsync(
        () => offlineConversationContents.readIfLatestAsync(item.conversationId, () => item.contentUpdatedAt),
        {source: TaskSourcesEnum.PREFETCH},
      ),
    ).pipe(
      // Ignore errors.
      catchError(() => of(undefined)),
      // Report cache hits for instrumentation.
      tap((cacheResult) => {
        if (cacheResult) {
          onCachedItem(item);
        }
      }),
      // Filter for cache misses.
      filter((cacheResult) => !cacheResult),
      // Emit the prefetcher item.
      mapTo(item),
    ),
  );
}

function createPrefetchQueue(
  state$: BehaviorSubject<AppState>,
  items$: Observable<PrefetchItem>,
): PriorityQueue<PrefetchItem> {
  const prefetchQueue = createPriorityQueueWithComparator(ordConversationContentItem.compare);

  state$
    .pipe(
      map(getHasNewConversationContentPrefetch),
      distinctUntilChanged(),
      switchMap((hasPrefetch) => enqueueItems(prefetchQueue)(hasPrefetch ? items$ : EMPTY)),
    )
    .subscribe();

  return prefetchQueue;
}

/** Clears the given queue upon subscription and pushes emitted items onto the queue as a side-effect. */
function enqueueItems<T>(queue: PriorityQueue<T>): MonoTypeOperatorFunction<T> {
  return (source$) =>
    defer(() => {
      queue.clear();

      return source$.pipe(
        tap((item) => {
          queue.push(item);
        }),
      );
    });
}

function popOutdatedPrefetchItems(queue: PriorityQueue<PrefetchItem>, state: AppState) {
  const userId = getUserId(state);
  const conversations = getConversations(state);

  while (queue.length > 0 && isOutdatedPrefetchItem(userId, conversations, queue.peek())) {
    queue.pop();
  }
}

/** Determines whether the given queue item is outdated given the current state. */
function isOutdatedPrefetchItem(
  userId: number,
  conversations: ResourceMap<ConversationModel>,
  item: PrefetchItem | undefined,
): boolean {
  if (!item) {
    return false;
  }

  const conversation = conversations[item.conversationId];
  const currentItem = maybeCreatePrefetchItem(userId, conversation);

  // The item is outdated if the current conversation should no longer be prefetched.
  return !currentItem;
}
